import { selectMerchantCapabilities, useAppSelector, useGetMerchantSettingsQuery } from 'mmfintech-backend-api'
import { useCallback, useEffect, useState } from 'react'
import { useUpdateTkbFeeDiscountMutation } from '../api/tkbFeeDiscountApi'

export type UseTkbFeeDiscount = {
  /** Whether the merchant may see the TKB fee-discount toggle at all. */
  eligible: boolean
  /** Current toggle state (use-the-Kingdom-Coin-for-fees ON/OFF). */
  active: boolean
  /** Persist a new toggle state; optimistic with revert-on-error. */
  onToggle: (next: boolean) => void
  /** True while the setting is loading or a persist is in flight. */
  disabled: boolean
}

/**
 * Single source of truth for the TKB ("Kingdom Coin") fee-discount toggle.
 *
 * Eligibility is driven solely by the merchant capability `enableTkb`. We do
 * NOT gate on TKB balance — the backend converts the fee to TKB at initiate
 * time, so showing the toggle off the capability alone matches product intent.
 *
 * The persisted value lives in `MerchantSettings.tkbFeeDiscountEnabled` and is
 * shared (via the RTK Query cache) across every placement of the toggle —
 * Exchange, Intra-account transfer and the dashboard — so flipping it in one
 * place is immediately reflected everywhere.
 */
export const useTkbFeeDiscount = (): UseTkbFeeDiscount => {
  const capabilities = useAppSelector(selectMerchantCapabilities)
  // SDK's MerchantCapabilities type lags the BFF — `enableTkb` exists on the
  // response per swagger but isn't declared on the typed interface yet.
  const eligible = Boolean((capabilities as any)?.enableTkb)

  // SDK's MerchantSettingsDTO type lags the BFF — tkbFeeDiscountEnabled exists
  // on the response per swagger but isn't declared on the typed interface yet.
  const { data: merchantSettings, isFetching: settingsFetching } = useGetMerchantSettingsQuery()
  const persisted = (merchantSettings as any)?.tkbFeeDiscountEnabled

  const [updateTkbFeeDiscount, { isLoading: persisting }] = useUpdateTkbFeeDiscountMutation()

  const [active, setActive] = useState<boolean>(false)
  const [hasHydrated, setHasHydrated] = useState<boolean>(false)

  // One-time hydration from the persisted setting. Guarded by `eligible` so we
  // never copy a value into local state for a merchant that can't see the
  // toggle anyway. After hydration runs, in-session toggles drive; a late
  // refetch can't clobber them.
  useEffect(() => {
    if (!hasHydrated && persisted !== undefined && eligible) {
      setActive(Boolean(persisted))
      setHasHydrated(true)
    }
  }, [persisted, hasHydrated, eligible])

  // Reset the toggle when eligibility goes away so we never act on a stale ON state.
  useEffect(() => {
    if (!eligible && active) {
      setActive(false)
    }
  }, [eligible, active])

  const onToggle = useCallback(
    (next: boolean) => {
      const previous = active
      setActive(next)
      updateTkbFeeDiscount(next)
        .unwrap()
        .catch(() => setActive(previous))
    },
    [active, updateTkbFeeDiscount]
  )

  return {
    eligible,
    active,
    onToggle,
    disabled: persisting || settingsFetching
  }
}
